import type { QueryClient } from '@tanstack/react-query';
import { createRootRouteWithContext, Outlet } from '@tanstack/react-router';
import { TanStackRouterDevtools } from '@tanstack/router-devtools';
import { Toaster } from 'sonner';
import { LobuLensProvider } from '@/components/lens/lobu-lens';
import { ResponsiveAppShell } from '@/components/shell/responsive-app-shell';
import { TooltipProvider } from '@/components/ui/tooltip';
import { useAuthState } from '@/lib/auth-state';
import { resolveUrlOrgSlug } from '@/lib/subdomain';
import { useInvalidationSSE } from '@/lib/use-invalidation-sse';
import { useLocalInstallAutoSignIn } from '@/lib/use-local-install-auto-signin';
import { usePreserveViewAsVisitor } from '@/lib/view-as';

interface RouterContext {
  queryClient: QueryClient;
}

export const Route = createRootRouteWithContext<RouterContext>()({
  component: RootLayout,
});

function RootLayout() {
  const { session, isReady: authReady } = useAuthState();
  // Keep `?view_as=visitor` across in-app link clicks (same-org only).
  usePreserveViewAsVisitor();
  // Single-user local installs: auto sign-in over loopback on any route the
  // login page doesn't already cover (e.g. a deep-linked /<org>/agents).
  useLocalInstallAutoSignIn();
  // OAuth/device pages render without the app sidebar/header. The
  // Chrome extension's sidepanel iframes the whole app at `/` now —
  // no special standalone mode for it.
  // Read the URL bar, not the router pathname: on per-org subdomains the
  // subdomain-history adapter prefixes the owner for the router, so a router
  // `/buremba/connector/…` would never match `/connector/`.
  const path = typeof window !== 'undefined' ? location.pathname : '/';
  // OAuth/device and the provider connection-claim page render without the app
  // sidebar/header — the claim link is an entry point for people who may not be
  // in the app yet. `/connector/…` is the generic claim route (e.g.
  // `/connector/slack/connection`); `/slack/` is its legacy redirect alias.
  const isStandalonePage =
    path.startsWith('/oauth/') ||
    path.startsWith('/slack/') ||
    path.startsWith('/connector/');

  // Invalidation SSE for the org the URL addresses. `resolveUrlOrgSlug` is the
  // single URL→org authority (subdomain first, else path); it returns null on
  // non-org routes (auth/oauth/connector/orgs/…), so the stream never 404-loops
  // against `/api/<route-word>/events`.
  const orgSlug = resolveUrlOrgSlug();
  useInvalidationSSE(authReady && session ? orgSlug : null);

  // Standalone pages (connect flow) render without sidebar/header
  if (isStandalonePage) {
    return (
      <div className="min-h-screen bg-background text-foreground">
        <Outlet />
      </div>
    );
  }

  // No full-screen auth gate: render the chrome (sidebar + header) immediately
  // so each section can show its own skeleton while data loads. Components
  // inside read `session` directly and handle the unauthenticated state.

  return (
    <LobuLensProvider>
      <TooltipProvider delayDuration={200}>
        <Toaster richColors position="bottom-right" />
        <ResponsiveAppShell>
          <Outlet />
        </ResponsiveAppShell>
        {import.meta.env.DEV ? <TanStackRouterDevtools position="bottom-right" /> : null}
      </TooltipProvider>
    </LobuLensProvider>
  );
}
